import {
    FRAGMENT_LISTING,
    FRAGMENT_LISTING_VARIANT,
} from '@marketplace/graphql/config/shared-fragments.js'
import gql from 'graphql-tag'

export const GET_VIEWERS_CART_SUMMARY_QUERY = gql`
    query getViewersCartSummary($cartIds: [BigInt]) {
        summary: viewer {
            cartsCount
            totalProductsInCartCount
            carts {
                id
                isCheckoutPossible
                minimumOrderAmount
                subtotalPrice
                status
                storefronts {
                    id
                    name
                    slug
                }
            }
            account {
                vouchers {
                    id
                    amount
                    minimumRetailerOrderTotal
                    isPercentage
                    startsAt
                    endsAt
                    appliesTo
                    currency
                    label
                    type
                    filterValue {
                        filterAttributeSlug
                        slug
                        name
                    }
                    storefront {
                        name
                        slug
                    }
                }
                country
                availableCredit
                payAfterArrivalDueDays
                hasPayAfterArrival
                hasGlobalFreeShipping
                globalFreeShippingThreshold
                isInNoFreeShippingExperiment
            }
            checkoutSummary(cartIds: $cartIds) {
                shippingPrice
                oldRetailerShippingPrice
                freeShippingThreshold
                subtotalPrice
                originalSubtotalPrice
                orderDiscountAmount
                productDiscountAmount
                taxPrice
                totalPrice
                totalProductsCount
                hasGlobalFreeShipping
                amountDueForGlobalFreeShipping
                shippingSurchargePrice
                qualifiesForFreeShippingEvent
                hasImportDuties
                hasTaxShiftableOrder
                voucherPrice
                subtotalForVoucherPrice
                processingFeePrice
                availablePaymentMethods {
                    id
                    name
                    logos
                    description
                }
                retailerVoucher {
                    id
                    name
                    amount
                    isPercentage
                    type
                    label
                    currency
                    minimumRetailerOrderTotal
                    appliesTo
                    isSupercharged
                    filterValue {
                        filterAttributeSlug
                        slug
                        name
                    }
                    storefront {
                        name
                        slug
                    }
                }
                voucherCode {
                    id
                    amount
                    isPercentage
                    label
                    currency
                    minimumRetailerOrderTotal
                    appliesTo
                    endsAt
                    type
                    filterValue {
                        filterAttributeSlug
                        slug
                        name
                    }
                    storefront {
                        name
                        slug
                    }
                }
                orderchampPlusSavings {
                    shipping
                    products
                    total
                }
            }
            unpaidRetailerOrder {
                id
                databaseId
                number
                totalPrice
            }
        }
    }
`

export const GET_VIEWERS_CART_BY_STOREFRONT_QUERY = gql`
    ${FRAGMENT_LISTING}
    ${FRAGMENT_LISTING_VARIANT}
    query getViewersCartByStorefrontQuery($slug: String!) {
        storefront: storefrontBySlug(slug: $slug) {
            globalDiscount {
                id
                percentage
                orderchampContributionPercentage
            }
            name
            minimumOrderAmount
            minimumOrderAmountReturningCustomer
            isReturningCustomer
            isReturningStorefrontCustomer
            shippingFreeAbove
            supplierFreeShippingAbove
            shippingPrice
            orderDiscounts {
                id
                percentage
                customerGroupType
                minimumOrderValue
                ends_at
                status
                campaign
            }

            specialDiscount {
                id
                percentage
                customerGroupType
            }
            isPublished
        }
        viewer {
            cartsCount
            storefrontSlugsInCart
            account {
                hasGlobalFreeShipping
                isInNoFreeShippingExperiment
            }
            total: checkoutSummary {
                shippingPrice
                subtotalPrice
                originalSubtotalPrice
                shippingSurchargePrice
                hasGlobalFreeShipping
                freeShippingThreshold
                orderDiscountAmount
                taxPrice
                totalPrice
            }
            cart: cartByStorefrontSlug(slug: $slug) {
                id
                subtotalPrice
                oldSubtotalPrice
                shippingPrice
                minimumOrderAmount
                orderDiscountAmount
                shippingSurchargePrice
                products {
                    hasShippingSurcharge
                    storefront {
                        id
                        slug
                        name
                        shippingFreeAbove
                        shippingPrice
                    }
                    id
                    quantity
                    subtotalPrice
                    listingVariant {
                        ...FragmentListingVariant
                    }
                    listing {
                        ...FragmentListing
                        isReorderOnly
                    }
                }
            }
        }
    }
`

export const GET_SIDE_CART_VIEWERS_CARTS_QUERY = gql`
    query getSideCartViewersCarts {
        viewer {
            carts {
                shippingSurchargePrice
                subtotalPrice
                minimumOrderAmount
                products {
                    id
                    subtotalPrice
                    hasShippingSurcharge
                    storefront {
                        id
                        slug
                    }
                }
                storefronts {
                    id
                    slug
                    name
                    shippingFreeAbove
                    shippingPrice
                    storyImage {
                        transformedUrl(width: 128, height: 128, method: CROP)
                    }
                }
            }
        }
    }
`

const CART_FIELDS_FRAGMENT = gql`
    ${FRAGMENT_LISTING}
    ${FRAGMENT_LISTING_VARIANT}
    fragment CartFields on Cart {
        id
        status
        databaseId
        isTaxShifted
        isCheckoutPossible
        includeDuringCheckout
        minimumOrderAmount
        taxPrice
        totalPrice
        shippingPrice
        oldSubtotalPrice
        subtotalPrice
        orderDiscountAmount
        productDiscountAmount
        shippingSurchargePrice
        hasToPassCustoms
        storefronts {
            name
            slug
            minimumOrderAmount
            minimumReorderAmount
            shippingFreeAbove
            supplierFreeShippingAbove
            databaseId
            shippingPrice
            country
            storyImage {
                transformedUrl(width: 50, height: 50, method: CROP)
            }
            orderDiscounts {
                id
                percentage
                minimumOrderValue
                campaign
            }
        }
        products {
            id
            oldSubtotalPrice
            oldUnitPrice
            subtotalPrice
            unitPrice
            quantity
            hasShippingSurcharge
            storefront {
                name
                slug
            }
            listingVariant {
                ...FragmentListingVariant
            }
            listing {
                ...FragmentListing
                isReorderOnly
            }
        }
    }
`

export const GET_VIEWERS_CARTS_QUERY = gql`
    ${CART_FIELDS_FRAGMENT}
    query getViewersCarts {
        cart: viewer {
            account {
                country
                hasValidVatNumber
                hasPayAfterArrival
                isSmallBusiness
                type
                eoriNumber
            }
            carts {
                ...CartFields
            }
        }
    }
`

export const GET_VIEWERS_INITIAL_CARTS_QUERY = gql`
    ${CART_FIELDS_FRAGMENT}
    query getViewersInitialCarts($first: Int!) {
        cart: viewer {
            account {
                country
                hasValidVatNumber
                hasPayAfterArrival
                isSmallBusiness
                type
                eoriNumber
            }
            carts(first: $first) {
                ...CartFields
            }
        }
    }
`

export const GET_VIEWERS_REMAINING_CARTS_QUERY = gql`
    ${CART_FIELDS_FRAGMENT}
    query getViewersRemainingCarts($offset: Int!) {
        remainingCart: viewer {
            carts(offset: $offset) {
                ...CartFields
            }
        }
    }
`

export const GET_VIEWERS_CARTS_BY_IDS_QUERY = gql`
    ${CART_FIELDS_FRAGMENT}
    query getViewersCartsByIds($cartIds: [BigInt]!) {
        viewer {
            carts(cartIds: $cartIds) {
                ...CartFields
            }
        }
    }
`

export const UPDATE_CART_PRODUCT_QUANTITY_MUTATION = gql`
    mutation updateCartProductQuantity($id: ID!, $quantity: Int!) {
        cartProductUpdate(input: { quantity: $quantity, cartProductId: $id }) {
            userErrors {
                field
                message
            }
        }
    }
`

export const UPDATE_CART_PRODUCTS_QUANTITY_MUTATION = gql`
    mutation updateCartProductsQuantity(
        $products: [CartProductsUpdateBulkInput!]!
    ) {
        cartProductsUpdate(input: { products: $products }) {
            userErrors {
                field
                message
            }
        }
    }
`

export const VALIDATE_VAT_MUTATION = gql`
    mutation validateVat {
        validateVat {
            success
        }
    }
`

export const DELETE_CART_PRODUCT_MUTATION = gql`
    mutation deleteCartProductMutation($id: ID!) {
        cartProductDelete(input: { cartProductId: $id }) {
            userErrors {
                field
                message
            }
        }
    }
`

export const DELETE_CART_STOREFRONT_MUTATION = gql`
    mutation cartStorefrontDeleteMutation($id: ID!) {
        cartStorefrontDelete(input: { storefrontId: $id }) {
            userErrors {
                field
                message
            }
        }
    }
`

export const ADD_PRODUCTS_TO_CART_MUTATION = gql`
    mutation addProductsToCartMutation(
        $products: [CartProductsCreateBulkInput!]
    ) {
        cartProductsCreate(input: { products: $products }) {
            userErrors {
                field
                message
            }
        }
    }
`

export const ADD_PRODUCT_TO_CART_MUTATION = gql`
    mutation addProductToCartMutation($product: CartProductCreateInput!) {
        cartProductCreate(input: $product) {
            userErrors {
                field
                message
            }
        }
    }
`

export const CHANGE_CART_SELECTION_STATE_MUTATION = gql`
    mutation changeCartSelectionStateMutation(
        $id: ID!
        $state: Boolean!
        $disableOtherCarts: Boolean
    ) {
        cartUpdate(
            input: {
                id: $id
                includeDuringCheckout: $state
                disableOtherCarts: $disableOtherCarts
            }
        ) {
            userErrors {
                field
                message
            }
        }
    }
`

export const CARTS_VIEWED_EVENT_MUTATION = gql`
    mutation cartsViewedMutation {
        cartsViewedEvent(input: {}) {
            userErrors {
                field
                message
            }
        }
    }
`

export const CARTS_UPDATE_RETAILER_VOUCHER_MUTATION = gql`
    mutation cartsUpdateRetailerVoucherMutation($voucher: ID!) {
        retailerOrderSetRetailerVoucher(
            input: { retailerVoucherId: $voucher }
        ) {
            success
            userErrors {
                field
                message
            }
        }
    }
`

export const CARTS_DELETE_RETAILER_VOUCHER_MUTATION = gql`
    mutation cartsDeleteRetailerVoucherMutation {
        retailerOrderRemoveRetailerVoucher(input: {}) {
            success
            userErrors {
                field
                message
            }
        }
    }
`

export const CARTS_DELETE_VOUCHER_MUTATION = gql`
    mutation cartsDeleteVoucherMutation {
        retailerOrderRemoveVoucherCode(input: {}) {
            success
            userErrors {
                field
                message
            }
        }
    }
`
